/** Pure 2D geometry helpers used by the spatial system (no world state). */
import type { Controller, Polygon, Radians, Rect, Vec2, WorldShape } from '../contracts';
import { BACKPEDAL_FACTOR } from '../contracts';

export const dist = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);

/** Wrap an angle to (-PI, PI]. */
export function wrapAngle(a: Radians): Radians {
  let r = a % (2 * Math.PI);
  if (r > Math.PI) r -= 2 * Math.PI;
  else if (r <= -Math.PI) r += 2 * Math.PI;
  return r;
}

export const angleTo = (from: Vec2, to: Vec2): Radians => Math.atan2(to.y - from.y, to.x - from.x);

/**
 * A move more than ~100° off the facing counts as backpedalling (cos 100°): a strafe (90°) stays
 * full speed even when the client's facing and the sim's differ by a little (WoW: only real
 * backward movement is slowed; S + A / S + D diagonals are).
 */
export const BACKPEDAL_DOT = -Math.sin((10 * Math.PI) / 180);

/**
 * Move speed multiplier for a direction-mode move along `dir` while facing `facing`: a human
 * moving against its facing (more than ~100° off it) backpedals at BACKPEDAL_FACTOR. Bots / NPCs: 1.
 */
export function backpedalFactor(controller: Controller, dir: Vec2, facing: Radians): number {
  if (controller !== 'human') return 1;
  return dir.x * Math.cos(facing) + dir.y * Math.sin(facing) < BACKPEDAL_DOT ? BACKPEDAL_FACTOR : 1;
}

/** Even-odd point-in-polygon test (boundary counts as inside within floating error). */
export function pointInPolygon(p: Vec2, poly: Polygon): boolean {
  const pts = poly.points;
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i]!;
    const b = pts[j]!;
    if (a.y > p.y !== b.y > p.y) {
      const x = ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x;
      if (p.x < x) inside = !inside;
    }
  }
  return inside;
}

export function polygonBounds(poly: Polygon): Rect {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of poly.points) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

export const rectContains = (r: Rect, p: Vec2, pad = 0): boolean =>
  p.x >= r.x - pad && p.x <= r.x + r.w + pad && p.y >= r.y - pad && p.y <= r.y + r.h + pad;

/** Closest point to p on segment ab. */
export function closestOnSegment(p: Vec2, a: Vec2, b: Vec2): Vec2 {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
  return { x: a.x + dx * t, y: a.y + dy * t };
}

/** Closest point to p on the polygon's boundary. */
export function closestOnPolygonEdge(p: Vec2, poly: Polygon): Vec2 {
  const pts = poly.points;
  let best: Vec2 = pts[0] ?? p;
  let bestD = Infinity;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const q = closestOnSegment(p, pts[j]!, pts[i]!);
    const d = dist(p, q);
    if (d < bestD) {
      bestD = d;
      best = q;
    }
  }
  return best;
}

/** Express p in the shape's local frame: `along` the facing, `across` to its left. */
function local(area: WorldShape, p: Vec2): { along: number; across: number } {
  const dx = p.x - area.origin.x;
  const dy = p.y - area.origin.y;
  const c = Math.cos(area.facing);
  const s = Math.sin(area.facing);
  return { along: dx * c + dy * s, across: -dx * s + dy * c };
}

/**
 * Hit test: does a circle of `radius` at p overlap the placed shape?
 * - circle/ring: centred on origin.
 * - cone: apex at origin, opening `angle` (total) around facing, length `range`.
 * - line: starts at origin, runs `length` along facing, `width` wide.
 * - rect: centred on origin, `h` along facing, `w` across.
 */
export function shapeContains(area: WorldShape, p: Vec2, radius = 0): boolean {
  const s = area.shape;
  switch (s.type) {
    case 'circle':
      return dist(area.origin, p) <= s.radius + radius;
    case 'ring': {
      const d = dist(area.origin, p);
      return d <= s.outer + radius && d >= s.inner - radius;
    }
    case 'cone': {
      const d = dist(area.origin, p);
      if (d > s.range + radius) return false;
      if (d <= radius) return true;
      const half = s.angle / 2;
      if (Math.abs(wrapAngle(angleTo(area.origin, p) - area.facing)) <= half) return true;
      if (radius <= 0) return false;
      // Circle may still clip one of the two edge rays.
      for (const sign of [1, -1]) {
        const a = area.facing + sign * half;
        const end = { x: area.origin.x + Math.cos(a) * s.range, y: area.origin.y + Math.sin(a) * s.range };
        if (dist(p, closestOnSegment(p, area.origin, end)) <= radius) return true;
      }
      return false;
    }
    case 'line': {
      const l = local(area, p);
      return l.along >= -radius && l.along <= s.length + radius && Math.abs(l.across) <= s.width / 2 + radius;
    }
    case 'rect': {
      const l = local(area, p);
      return Math.abs(l.along) <= s.h / 2 + radius && Math.abs(l.across) <= s.w / 2 + radius;
    }
  }
}

/** Rough bounding radius of a placed shape around its origin (for candidate searches). */
export function shapeReach(area: WorldShape): number {
  const s = area.shape;
  switch (s.type) {
    case 'circle':
      return s.radius;
    case 'ring':
      return s.outer;
    case 'cone':
      return s.range;
    case 'line':
      return Math.hypot(s.length, s.width / 2);
    case 'rect':
      return Math.hypot(s.w, s.h) / 2;
  }
}
